import type { SenatorInfo, AreaGeoJSON, PlaceLocation } from './types';

// Declare Google Maps callback on Window
declare global {
    interface Window {
        google: typeof google;
        initGoogleMapsCallback?: () => void;
    }
}

const SENATOR_CACHE_KEY = 'fys_senators_v1';
const SENATOR_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface SenatorCache {
    fetchedAt: number;
    data: SenatorInfo[];
}

/**
 * Data service for Find Your Senator map functionality
 */
export class FindYourSenatorDataService {

    /**
     * Returns the full senator list, using localStorage as a 24-hour cache.
     * Falls back to a live API call if the cache is absent, expired, or unavailable.
     */
    private static async getAllSenators(): Promise<SenatorInfo[]> {
        try {
            const raw = localStorage.getItem(SENATOR_CACHE_KEY);
            if (raw) {
                const cache: SenatorCache = JSON.parse(raw);
                if (Date.now() - cache.fetchedAt < SENATOR_CACHE_TTL_MS) {
                    return cache.data;
                }
            }
        } catch {
            // localStorage unavailable or corrupt — fall through to fetch
        }

        const response = await fetch('/api/FindYourSenatorApi/senators/all');
        if (!response.ok) {
            throw new Error('Failed to fetch senator list');
        }

        const data: SenatorInfo[] = await response.json();

        try {
            const cache: SenatorCache = { fetchedAt: Date.now(), data };
            localStorage.setItem(SENATOR_CACHE_KEY, JSON.stringify(cache));
        } catch {
            // Storage full or unavailable — continue without caching
        }

        return data;
    }

    /**
     * Fetches the GeoJSON data for Michigan Senate districts
     */
    public static async getDistrictsGeoJSON(): Promise<AreaGeoJSON> {
        const response = await fetch('/data/michigan-senate-districts_2024.geojson');
        if (!response.ok) {
            throw new Error('Failed to load district boundaries');
        }
        return response.json();
    }

    public static async getCitiesGeoJSON(): Promise<AreaGeoJSON> {
        const response = await fetch('/data/MichiganCities.geojson');
        if (!response.ok) {
            throw new Error('Failed to load city boundaries');
        }
        return response.json();
    }

    /**
     * Fetches the GeoJSON data for Michigan ZIP code boundaries.
     * This file is large (~6 MB) and should only be fetched on demand.
     */
    public static async getZipGeoJSON(): Promise<AreaGeoJSON> {
        const response = await fetch('/data/MichiganZipCodes.geojson');
        if (!response.ok) {
            throw new Error('Failed to load ZIP code boundaries');
        }
        return response.json();
    }


    /**
     * Fetches senator information by district number
     * @param districtNumber The district number (e.g., "8" or "08")
     */
    public static async getSenatorByDistrict(districtNumber: string): Promise<SenatorInfo | null> {
        const normalizedDistrict = districtNumber.replace(/^0+/, '');
        const senators = await FindYourSenatorDataService.getAllSenators();        
        return senators.find(s => s.district.replace(/^0+/, '') === normalizedDistrict) ?? null;
    }

    /**
     * Fetches senator information for multiple districts in a single request.
     * Missing districts are silently omitted from the response.
     * @param districtNumbers Array of district numbers (leading zeros are stripped)
     */
    public static async getSenatorsByDistricts(districtNumbers: string[]): Promise<SenatorInfo[]> {
        const normalized = districtNumbers
            .map(d => d.replace(/^0+/, ''))
            .filter(d => d.length > 0);

        if (normalized.length === 0) {
            return [];
        }

        const senators = await FindYourSenatorDataService.getAllSenators();
        return normalized
            .map(d => senators.find(s => s.district.replace(/^0+/, '') === d))
            .filter((s): s is SenatorInfo => s !== undefined)
            .sort((a, b) => a.lastName.localeCompare(b.lastName));
    }

    /**
     * Fetches the location for a Place Prediction using the Google Places API (v2).
     * Calling fetchFields on the Place returned from prediction.toPlace() ends the
     * autocomplete session associated with the session token used in the request.
     * @param prediction The PlacePrediction returned from AutocompleteSuggestion
     */
    public static async geocodePlace(prediction: google.maps.places.PlacePrediction): Promise<PlaceLocation | null> {
        if (!window.google?.maps?.places) {
            throw new Error('Google Maps API not loaded');
        }

        const place = prediction.toPlace();
        await place.fetchFields({ fields: ['location', 'formattedAddress', 'types'] });

        if (!place.location) {
            return null;
        }

        // Place types that indicate a city, village, or township (not a street address)
        const cityOrTownshipTypes = new Set([
            'locality',
            'sublocality',
            'administrative_area_level_3', // townships
            'administrative_area_level_4',
            'postal_town',
            'neighborhood',
        ]);

        const placeTypes: string[] = place.types ?? [];
        const isCityOrTownship = placeTypes.some(t => cityOrTownshipTypes.has(t));
        const isZipCode = placeTypes.includes('postal_code');

        return {
            lat: place.location.lat(),
            lng: place.location.lng(),
            formattedAddress: place.formattedAddress ?? '',
            isCityOrTownship,
            isZipCode,
            placeTypes
        };
    }

    /**
     * Geocodes a free-form address string using the Google Maps Geocoder API.
     * Use this as a fallback when autocomplete returns no suggestions.
     * @param address The address string entered by the user
     */
    public static async geocodeAddress(address: string): Promise<PlaceLocation | null> {
        if (!window.google?.maps) {
            throw new Error('Google Maps API not loaded');
        }

        const geocoder = new google.maps.Geocoder();
        const result = await geocoder.geocode({
            address,
            componentRestrictions: {
                country: 'US',
                administrativeArea: 'Michigan',
            },
        });

        if (!result.results || result.results.length === 0) {
            return null;
        }

        const best = result.results[0];
        const location = best.geometry.location;
        const types: string[] = best.types ?? [];

        // Reject results that are too broad to identify a district (e.g. "Michigan, USA")
        if (types.includes('administrative_area_level_1') || types.includes('country')) {
            return null;
        }

        // Guard against the geocoder returning a result outside Michigan
        if (!FindYourSenatorDataService.isPointInMichigan(location.lat(), location.lng())) {
            return null;
        }

        const cityOrTownshipTypes = new Set([
            'locality',
            'sublocality',
            'administrative_area_level_3',
            'administrative_area_level_4',
            'postal_town',
            'neighborhood',
        ]);

        return {
            lat: location.lat(),
            lng: location.lng(),
            formattedAddress: best.formatted_address ?? address,
            isCityOrTownship: types.some(t => cityOrTownshipTypes.has(t)),
            isZipCode: types.includes('postal_code'),
            placeTypes: types,
        };
    }

    /**
     * Checks if a point is within Michigan's approximate boundaries.
     * Uses separate bounding boxes for the Lower Peninsula (LP) and Upper Peninsula (UP)
     * to reduce false positives from neighboring states and Canadian waters that fall
     * inside a single rectangular box around the whole state.
     *
     * LP: tall rectangle from the Indiana/Ohio border up to the Mackinac Straits area.
     * UP: wide east–west rectangle from Wisconsin to the Ontario/Lake Huron shore.
     */
    public static isPointInMichigan(lat: number, lng: number): boolean {
        // Lower Peninsula
        // Western boundary extended to -87.2 to include all of Berrien County
        // (southwestern tip near New Buffalo extends to approximately -86.82).
        const inLP =
            lat >= 41.696 && lat <= 46.0 &&
            lng >= -87.2  && lng <= -82.122;

        // Upper Peninsula
        const inUP =
            lat >= 45.0  && lat <= 47.50 &&
            lng >= -90.418 && lng <= -83.4;

        return inLP || inUP;
    }
}
